import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Buildings } from "@phosphor-icons/react";
import { useBuildingsAndUnits } from "hooks/useBuildingsAndUnits";

interface UseRenovationDocumentsParams {
  initialFilters?: {
    documentType?: string;
    documentStatus?: string;
    projectId?: number;
    projectPartId?: number;
  };
  currentBuildingId?: string;
  onBuildingChange?: (buildingId: string) => void;
  navigate?: (path: string) => void;
  /**
   * Forces the projectId filter to a fixed value — used when this hook drives
   * the embedded financial-documents view inside a project detail page.
   * When set, the "Project" filter chip is hidden from the FilterBar.
   */
  lockedProjectId?: number;
  /**
   * Forces the buildingId filter to a fixed value (parent-controlled scope).
   * Hides the "Gebouw" filter chip when set.
   */
  lockedBuildingId?: string;
  /**
   * Forces the projectPartId filter to a fixed value — used by the embedded
   * subset on the project part detail page.  Hides the "Project Part" chip.
   */
  lockedProjectPartId?: number;
}

/**
 * Hook for managing renovation documents filter state
 *
 * This hook handles filter state management and FilterBar configuration.
 * It does NOT handle data fetching - that's done by useRenovationDocumentsData.
 *
 * Filters are converted to API query params and passed to useRenovationDocumentsData,
 * which handles backend filtering via useLazyLoading.
 */
export const useRenovationDocuments = (
  params: UseRenovationDocumentsParams = {},
) => {
  const {
    initialFilters = {},
    currentBuildingId,
    onBuildingChange,
    navigate,
    lockedProjectId,
    lockedBuildingId,
    lockedProjectPartId,
  } = params;
  const { session } = useSession();
  // Source of truth for buildings is the global useBuildingsAndUnits hook so
  // that the filter dropdown lists EVERY building accessible to the user, not
  // just buildings that already have a renovation document.  The previous
  // /renovation/documents/buildings endpoint only returned buildings with
  // existing documents, which made it impossible to import the first invoice
  // for a brand-new building.
  const { buildings: allBuildings } = useBuildingsAndUnits();
  const buildings = useMemo(
    () =>
      (allBuildings ?? []).map((b) => ({
        id: b.id,
        name: b.company?.name,
      })),
    [allBuildings],
  );

  const [projects, setProjects] = useState<
    Array<{ id: number; title: string }>
  >([]);
  const [projectParts, setProjectParts] = useState<
    Array<{ id: number; title: string; projectId: number }>
  >([]);
  const [suppliers, setSuppliers] = useState<
    Array<{ id: number; name: string }>
  >([]);

  // Fetch projects from API - filtered by building if one is selected
  useEffect(() => {
    const fetchProjects = async () => {
      if (!session) return;

      try {
        // Use building-specific endpoint if a building is selected
        const endpoint = currentBuildingId
          ? `${API_URL}/renovation/project/building/${currentBuildingId}`
          : `${API_URL}/renovation/project/minimal`;

        const response = await fetchWithAuth(
          endpoint,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const data = await response.json();
          const projectList = data.map((project: any) => ({
            id: project.id,
            title: project.title || `Project ${project.id}`,
            buildingId: project.building?.id || project.buildingId,
          }));
          setProjects(projectList);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    fetchProjects();
  }, [session, currentBuildingId]);

  // Fetch project parts from API - filtered by available projects
  useEffect(() => {
    const fetchProjectParts = async () => {
      if (!session) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/project-part/minimal`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const data = await response.json();
          let partList = data.map((part: any) => ({
            id: part.id,
            title: part.title || `Part ${part.id}`,
            projectId: part.projectId,
          }));

          // Filter parts to only show those belonging to currently visible projects
          if (currentBuildingId && projects.length > 0) {
            const projectIds = projects.map(p => p.id);
            partList = partList.filter((part: any) => projectIds.includes(part.projectId));
          }

          setProjectParts(partList);
        }
      } catch (error) {
        console.error("Error fetching project parts:", error);
      }
    };

    fetchProjectParts();
  }, [session, currentBuildingId, projects]);

  // Fetch suppliers from API (ContactCompany - same as used on documents)
  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!session) return;

      try {
        // Use contact/companies endpoint to get ContactCompany records
        // These are the same companies used as suppliers on renovation documents
        const response = await fetchWithAuth(
          `${API_URL}/contact/companies`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const data = await response.json();
          const supplierList = data.map((company: any) => ({
            id: company.id,
            name: company.name || `Company ${company.id}`,
          }));
          setSuppliers(supplierList);
        }
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    };

    fetchSuppliers();
  }, [session]);

  // Generate year options (current year and 5 years back)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      const year = currentYear - i;
      years.push({ key: year.toString(), label: year.toString() });
    }
    return years;
  }, []);

  // Filter states
  const [searchText, setSearchText] = useState("");

  // Smart shortcut: when the user types something that exactly matches a known
  // building.id, jump straight to that building's scope (sets ?building=… in
  // the URL) instead of running a textual search.  This makes it possible to
  // navigate to a building with zero documents — the original blocker for
  // importing the very first invoice.  Only triggers on EXACT case-insensitive
  // match to avoid hijacking partial searches.
  const lastJumpedBuildingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!searchText) return;
    const normalized = searchText.trim();
    if (!normalized) return;
    if (!buildings.length) return;
    if (currentBuildingId && currentBuildingId.toLowerCase() === normalized.toLowerCase()) return;

    const match = buildings.find(
      (b) => b.id.toLowerCase() === normalized.toLowerCase(),
    );
    if (!match) return;
    if (lastJumpedBuildingRef.current === match.id) return;

    lastJumpedBuildingRef.current = match.id;
    setSearchText("");
    if (onBuildingChange) onBuildingChange(match.id);
  }, [searchText, buildings, currentBuildingId, onBuildingChange]);
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    type: initialFilters.documentType ? [initialFilters.documentType] : [],
    status: initialFilters.documentStatus
      ? [initialFilters.documentStatus]
      : [],
    project: initialFilters.projectId
      ? [initialFilters.projectId.toString()]
      : [],
    projectPart: initialFilters.projectPartId
      ? [initialFilters.projectPartId.toString()]
      : [],
    supplier: [],
    year: [],
    odooStatus: [],
    paymentStatus: [],
  });
  const [isRestoringFromURL, setIsRestoringFromURL] = useState(false);

  // Convert filter state to API query params format.
  // lockedProjectId (when set) wins over both the active filter chip and the
  // initial URL-derived projectId so embedded views can guarantee scope.
  const apiFilters = useMemo(() => {
    const filters = {
      search: searchText,
      documentType: activeFilters.type || [],
      documentStatus: activeFilters.status || [],
      projectId:
        lockedProjectId ??
        (activeFilters.project?.length
          ? Number(activeFilters.project[0])
          : initialFilters.projectId),
      projectPartId:
        lockedProjectPartId ??
        (activeFilters.projectPart?.length
          ? Number(activeFilters.projectPart[0])
          : initialFilters.projectPartId),
      supplierId: activeFilters.supplier?.length
        ? Number(activeFilters.supplier[0])
        : undefined,
      year: activeFilters.year?.length
        ? Number(activeFilters.year[0])
        : undefined,
      odooStatus: activeFilters.odooStatus?.length
        ? activeFilters.odooStatus[0]
        : undefined,
      paymentStatus: activeFilters.paymentStatus?.length
        ? activeFilters.paymentStatus[0]
        : undefined,
    };
    return filters;
  }, [searchText, activeFilters, initialFilters, lockedProjectId, lockedProjectPartId]);

  // Filter bar configuration.  When a scope is locked by the parent (embedded
  // mode) the corresponding chip is filtered out below so the user cannot
  // override it.
  const filterBarSections = useMemo(
    () => {
      const sections = [
      {
        key: "building",
        label: "Gebouw",
        options: buildings.map((building) => ({
          key: building.id,
          label: `${building.id}${building.name ? ` - ${building.name}` : ""}`,
        })),
        selectedValues: currentBuildingId ? [currentBuildingId] : [],
        onUpdate: (values: string[]) => {
          // Clear project and project part filters when building changes
          setActiveFilters((prev) => ({
            ...prev,
            project: [],
            projectPart: [],
          }));

          // Single select for building
          if (values.length > 0 && onBuildingChange) {
            onBuildingChange(values[0]);
          } else if (values.length === 0) {
            // Clear building filter - remove both old and new URL parameters
            if (navigate) {
              const searchParams = new URLSearchParams(window.location.search);
              searchParams.delete("building"); // Remove new standard param
              searchParams.delete("buildingId"); // Remove old param (backward compatibility)
              const newUrl = `/renovation/documents${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
              navigate(newUrl);
            } else if (onBuildingChange) {
              // Fallback to callback if navigate not provided
              onBuildingChange("");
            }
          }
        },
        color: "neutral" as const,
        singleSelect: true,
        renderOption: (option: any) => (
          <div className="flex items-center gap-2">
            <Buildings
              size={16}
              weight="duotone"
              className="text-brick-primary"
            />
            <span>{option.label}</span>
          </div>
        ),
      },
      {
        key: "project",
        label: "Project",
        options: projects.map((project) => ({
          key: project.id.toString(),
          label: `RENO-${project.id} - ${project.title}`,
        })),
        selectedValues: activeFilters.project || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            project: values,
          }));
        },
        color: "moss" as const,
        singleSelect: true,
      },
      {
        key: "projectPart",
        label: "Project Part",
        options: projectParts.map((part) => ({
          key: part.id.toString(),
          label: `RENO${part.projectId}-O${part.id} - ${part.title}`,
        })),
        selectedValues: activeFilters.projectPart || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            projectPart: values,
          }));
        },
        color: "moss" as const,
        singleSelect: true,
      },
      {
        key: "supplier",
        label: "Leverancier",
        options: suppliers.map((supplier) => ({
          key: supplier.id.toString(),
          label: supplier.name,
        })),
        selectedValues: activeFilters.supplier || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            supplier: values,
          }));
        },
        color: "neutral" as const,
        singleSelect: true,
      },
      {
        key: "type",
        label: "Document Type",
        options: [
          { key: "Offerte", label: "Offerte" },
          { key: "Factuur", label: "Factuur" },
          { key: "Bestelbon", label: "Bestelbon" },
          { key: "Contract", label: "Contract" },
          { key: "Overig", label: "Overig" },
        ],
        selectedValues: activeFilters.type || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            type: values,
          }));
        },
        color: "moss" as const,
      },
      {
        key: "status",
        label: "Status",
        options: [
          { key: "Concept", label: "Concept" },
          { key: "Ontvangen", label: "Ontvangen" },
          { key: "Goedgekeurd", label: "Goedgekeurd" },
          { key: "Afgewezen", label: "Afgewezen" },
        ],
        selectedValues: activeFilters.status || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            status: values,
          }));
        },
        color: "brick" as const,
      },
      {
        key: "year",
        label: "Jaar",
        options: yearOptions,
        selectedValues: activeFilters.year || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            year: values,
          }));
        },
        color: "neutral" as const,
        singleSelect: true,
      },
      {
        key: "odooStatus",
        label: "Odoo-status",
        options: [
          { key: "exists", label: "Nog in Odoo" },
          { key: "mismatch", label: "Bedrag wijkt af" },
          { key: "missing", label: "Niet meer in Odoo" },
        ],
        selectedValues: activeFilters.odooStatus || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            odooStatus: values,
          }));
        },
        color: "brick" as const,
        singleSelect: true,
      },
      {
        key: "paymentStatus",
        label: "Betaalstatus",
        options: [
          { key: "paid", label: "Betaald" },
          { key: "not_paid", label: "Niet betaald" },
          { key: "partial", label: "Gedeeltelijk" },
          { key: "in_payment", label: "In betaling" },
          { key: "reversed", label: "Tegengeboekt" },
        ],
        selectedValues: activeFilters.paymentStatus || [],
        onUpdate: (values: string[]) => {
          setActiveFilters((prev) => ({
            ...prev,
            paymentStatus: values,
          }));
        },
        color: "moss" as const,
        singleSelect: true,
      },
    ];
      // Strip locked scopes so embedded views cannot expose chips that would
      // let the user escape the parent-imposed scope.
      return sections.filter((s) => {
        if (lockedProjectId && s.key === "project") return false;
        if (lockedBuildingId && s.key === "building") return false;
        if (lockedProjectPartId && s.key === "projectPart") return false;
        return true;
      });
    },
    [
      activeFilters,
      buildings,
      projects,
      projectParts,
      suppliers,
      yearOptions,
      currentBuildingId,
      onBuildingChange,
      lockedProjectId,
      lockedBuildingId,
      lockedProjectPartId,
    ],
  );

  const filterBarChips = useMemo(() => {
    const chips: any[] = [];

    // Building filter chip — suppressed when the building scope is locked by
    // the parent (embedded mode).
    if (currentBuildingId && !lockedBuildingId) {
      const building = buildings.find((b) => b.id === currentBuildingId);
      chips.push({
        key: `building-${currentBuildingId}`,
        label: building ? building.id : currentBuildingId,
        category: "Gebouw",
        color: "neutral",
        onRemove: () => {
          // Clear building filter by navigating to page without building params
          if (navigate) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete("building"); // Remove new standard param
            searchParams.delete("buildingId"); // Remove old param (backward compatibility)
            const newUrl = `/renovation/documents${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
            navigate(newUrl);
          } else if (onBuildingChange) {
            // Fallback to callback if navigate not provided
            onBuildingChange("");
          }
        },
      });
    }

    // Project filter chip — suppressed when the project scope is locked.
    if (lockedProjectId) {
      // No-op: locked scope; do not render a chip.
    }
    activeFilters.project?.forEach((projectId) => {
      if (lockedProjectId) return;
      const project = projects.find((p) => p.id.toString() === projectId);
      chips.push({
        key: `project-${projectId}`,
        label: project
          ? `RENO-${project.id} - ${project.title}`
          : `Project ${projectId}`,
        category: "Project",
        color: "moss",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            project: prev.project.filter((p) => p !== projectId),
          }));
        },
      });
    });

    // Project Part filter chip — suppressed when the part scope is locked.
    activeFilters.projectPart?.forEach((partId) => {
      if (lockedProjectPartId) return;
      const part = projectParts.find((p) => p.id.toString() === partId);
      chips.push({
        key: `projectPart-${partId}`,
        label: part
          ? `RENO${part.projectId}-O${part.id} - ${part.title}`
          : `Part ${partId}`,
        category: "Project Part",
        color: "moss",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            projectPart: prev.projectPart.filter((p) => p !== partId),
          }));
        },
      });
    });

    // Supplier filter chip
    activeFilters.supplier?.forEach((supplierId) => {
      const supplier = suppliers.find((s) => s.id.toString() === supplierId);
      chips.push({
        key: `supplier-${supplierId}`,
        label: supplier ? supplier.name : `Supplier ${supplierId}`,
        category: "Leverancier",
        color: "neutral",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            supplier: prev.supplier.filter((s) => s !== supplierId),
          }));
        },
      });
    });

    // Type filters
    activeFilters.type?.forEach((type) => {
      chips.push({
        key: `type-${type}`,
        label: type,
        category: "Type",
        color: "moss",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            type: prev.type.filter((t) => t !== type),
          }));
        },
      });
    });

    // Status filters
    activeFilters.status?.forEach((status) => {
      chips.push({
        key: `status-${status}`,
        label: status,
        category: "Status",
        color: "brick",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            status: prev.status.filter((s) => s !== status),
          }));
        },
      });
    });

    // Year filter
    activeFilters.year?.forEach((year) => {
      chips.push({
        key: `year-${year}`,
        label: year,
        category: "Jaar",
        color: "neutral",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            year: prev.year.filter((y) => y !== year),
          }));
        },
      });
    });

    // Odoo-status filter
    activeFilters.odooStatus?.forEach((status) => {
      const odooStatusLabel =
        status === "missing"
          ? "Niet meer in Odoo"
          : status === "mismatch"
            ? "Bedrag wijkt af"
            : "Nog in Odoo";
      chips.push({
        key: `odooStatus-${status}`,
        label: odooStatusLabel,
        category: "Odoo-status",
        color: "brick",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            odooStatus: prev.odooStatus.filter((s) => s !== status),
          }));
        },
      });
    });

    // Payment-status filter
    activeFilters.paymentStatus?.forEach((status) => {
      const paymentLabel =
        status === "paid"
          ? "Betaald"
          : status === "not_paid"
            ? "Niet betaald"
            : status === "partial"
              ? "Gedeeltelijk"
              : status === "in_payment"
                ? "In betaling"
                : "Tegengeboekt";
      chips.push({
        key: `paymentStatus-${status}`,
        label: paymentLabel,
        category: "Betaalstatus",
        color: "moss",
        onRemove: () => {
          setActiveFilters((prev) => ({
            ...prev,
            paymentStatus: prev.paymentStatus.filter((s) => s !== status),
          }));
        },
      });
    });

    return chips;
  }, [
    activeFilters,
    currentBuildingId,
    buildings,
    projects,
    projectParts,
    suppliers,
    onBuildingChange,
    lockedProjectId,
    lockedBuildingId,
    lockedProjectPartId,
  ]);

  const handleFiltersFromURL = useCallback(
    (urlState: {
      search?: string;
      groupBy?: string;
      filterSections?: Record<string, string[]>;
    }) => {
      setIsRestoringFromURL(true);
      if (urlState.filterSections) {
        setActiveFilters(urlState.filterSections);
      }
      if (urlState.search) {
        setSearchText(urlState.search);
      }
      setTimeout(() => setIsRestoringFromURL(false), 100);
    },
    [],
  );

  return {
    // Filter states
    searchText,
    setSearchText,
    activeFilters,
    setActiveFilters,
    apiFilters,

    // FilterBar configuration
    filterBarSections,
    filterBarChips,
    isRestoringFromURL,
    handleFiltersFromURL,
  };
};
